import type { Project, Session, SessionStatus } from "@mux/sdk/v2/client"
import type { SidebarProjection } from "@/context/global-sync/types"
import type { ServerConnection } from "@/context/server"
import { jsonArray, jsonObject, jsonString } from "@/transport/decode"
import { expectJson, expectResponse } from "@/transport/http"
import { serverFetch } from "@/transport/server"
import { sessionDecoder } from "@/transport/session-decode"
import { decodeWorkspace } from "@/transport/workspace"

export type SessionPageResponse = { items: Session[]; nextCursor?: string; hasArchived?: boolean }
export type TrashedSession = Session & { time: Session["time"] & { deleted?: number } }

const decodeSession = sessionDecoder("Server returned an invalid session")

export function decodeSessionPage(value: unknown): SessionPageResponse {
  const page = jsonObject(value, "Server returned an invalid session page")
  if (page.hasArchived !== undefined && typeof page.hasArchived !== "boolean") {
    throw new Error("Server returned invalid archive presence")
  }
  return {
    ...(typeof page.hasArchived === "boolean" ? { hasArchived: page.hasArchived } : {}),
    items: [
      ...(page.items === null ? [] : jsonArray(page.items, "Server returned an invalid session page").map(decodeSession)),
      ...(page.snoozed === undefined ? [] : jsonArray(page.snoozed, "Server returned invalid snoozed sessions").map(decodeSession)),
    ],
    nextCursor:
      page.nextCursor === undefined || page.nextCursor === ""
        ? undefined
        : jsonString(page.nextCursor, "Server returned an invalid session page"),
  }
}

function decodeSidebarProjection(value: unknown): SidebarProjection {
  const projection = jsonObject(value, "Server returned an invalid sidebar projection")
  const statuses = jsonObject(projection.statuses, "Server returned an invalid sidebar projection")
  for (const status of Object.values(statuses)) jsonObject(status, "Server returned an invalid sidebar projection")
  return {
    sessions: jsonArray(projection.sessions, "Server returned an invalid sidebar projection").map(decodeSession),
    statuses: statuses as Record<string, SessionStatus>,
  }
}

export async function listTrashedSessions(server: ServerConnection.HttpBase, signal?: AbortSignal) {
  return expectJson(
    await serverFetch(server, "/session/trash", { signal }),
    (value) => jsonArray(value, "Server returned invalid trash contents").map(decodeSession) as TrashedSession[],
    { error: "Trash request failed" },
  )
}

export type TrashPreviewMessage = {
  info: { id: string; role: "user" | "assistant" }
  parts: Array<{ type?: string; text?: string }>
}

export type TrashPreview = {
  session: TrashedSession
  messages: TrashPreviewMessage[]
}

export async function getTrashPreview(server: ServerConnection.HttpBase, sessionID: string, signal?: AbortSignal) {
  return expectJson(
    await serverFetch(server, `/session/${encodeURIComponent(sessionID)}/trash-preview`, { signal }),
    (value): TrashPreview => {
      const preview = jsonObject(value, "Server returned an invalid trash preview")
      const session = decodeSession(preview.session) as TrashedSession
      const messages = jsonArray(preview.messages, "Server returned invalid trash preview messages").map((item): TrashPreviewMessage => {
        const message = jsonObject(item, "Server returned an invalid trash preview message")
        const info = jsonObject(message.info, "Server returned an invalid trash preview message")
        const role = jsonString(info.role, "Server returned an invalid trash preview message")
        if (role !== "user" && role !== "assistant") throw new Error("Server returned an invalid trash preview message")
        return {
          info: { id: jsonString(info.id, "Server returned an invalid trash preview message"), role },
          parts: jsonArray(message.parts, "Server returned an invalid trash preview message").map((part) => {
            const data = jsonObject(part, "Server returned an invalid trash preview part")
            return { type: typeof data.type === "string" ? data.type : undefined, text: typeof data.text === "string" ? data.text : undefined }
          }),
        }
      })
      return { session, messages }
    },
    { error: "Trash preview request failed" },
  )
}

export async function emptyTrash(server: ServerConnection.HttpBase) {
  const response = await serverFetch(server, "/session/trash", { method: "DELETE" })
  if (!response.ok) await expectJson(response, (value) => value, { error: "Empty trash request failed" })
}

export async function restoreTrashedSession(server: ServerConnection.HttpBase, sessionID: string) {
  return expectJson(
    await serverFetch(server, `/session/${encodeURIComponent(sessionID)}/restore`, { method: "POST" }),
    decodeSession,
    { error: "Restore chat request failed" },
  )
}

export async function permanentlyDeleteTrashedSession(server: ServerConnection.HttpBase, sessionID: string) {
  const response = await serverFetch(server, `/session/${encodeURIComponent(sessionID)}/permanent`, { method: "DELETE" })
  if (!response.ok) await expectJson(response, (value) => value, { error: "Permanent delete request failed" })
}

export async function getRootSessionPage(input: {
  server: ServerConnection.HttpBase
  directory: string
  limit: number
  cursor?: string
  signal?: AbortSignal
}) {
  const query = new URLSearchParams({ directory: input.directory, roots: "true", limit: String(input.limit) })
  if (input.cursor) query.set("cursor", input.cursor)
  return expectJson(
    await serverFetch(input.server, `/session/page?${query}`, { signal: input.signal }),
    decodeSessionPage,
    { error: "Session page request failed" },
  )
}

export async function getSessionsByID(server: ServerConnection.HttpBase, sessionIDs: readonly string[], signal?: AbortSignal) {
  const ids = [...new Set(sessionIDs)]
  signal?.throwIfAborted()
  // The session details endpoint accepts at most 64 IDs per request.
  const batches = Array.from({ length: Math.ceil(ids.length / 64) }, (_, index) => ids.slice(index * 64, index * 64 + 64))
  const pages = await Promise.all(batches.map(async (batch) => expectJson(
    await serverFetch(server, `/session/details?${new URLSearchParams({ ids: batch.join(",") })}`, { signal }),
    (value) => jsonArray(value, "Server returned invalid session details").map(decodeSession),
    { error: "Session details request failed" },
  )))
  return pages.flat()
}

export function getSidebarProjection(server: ServerConnection.HttpBase, signal?: AbortSignal) {
  return serverFetch(server, "/session/sidebar", { signal })
    .then((response) => expectJson(response, decodeSidebarProjection, { error: "Sidebar request failed" }))
}

function decodeSessionWaiting(value: unknown): Record<string, number> {
  const waiting = jsonObject(value, "Server returned invalid waiting chats")
  for (const launches of Object.values(waiting)) {
    if (!Number.isSafeInteger(launches) || (launches as number) < 1) throw new Error("Server returned invalid waiting chats")
  }
  return waiting as Record<string, number>
}

/** How many results each chat is still owed, from launches or background commands; chats with none are absent. */
export function getSessionWaiting(server: ServerConnection.HttpBase, signal?: AbortSignal) {
  return serverFetch(server, "/session/waiting", { signal })
    .then((response) => expectJson(response, decodeSessionWaiting, { error: "Waiting chats request failed" }))
}

export async function moveSessionToWorkspace(
  server: ServerConnection.HttpBase,
  sessionID: string,
  workspaceID: string,
) {
  return expectJson(
    await serverFetch(server, `/session/${encodeURIComponent(sessionID)}/move`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ workspaceID }),
    }),
    decodeSession,
    { error: "Move chat request failed" },
  )
}

export async function moveSessionToNewWorkspace(
  server: ServerConnection.HttpBase,
  sessionID: string,
  workspaceID: string,
) {
  return expectJson(
    await serverFetch(server, `/session/${encodeURIComponent(sessionID)}/move-to-new-workspace`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: workspaceID }),
    }),
    (value): { session: Session; workspace: Project } => {
      const invalid = "Server returned an invalid moved chat"
      const result = jsonObject(value, invalid)
      return { session: decodeSession(result.session), workspace: decodeWorkspace(result.workspace, invalid) }
    },
    { error: "Move chat request failed" },
  )
}

export async function snoozeSession(server: ServerConnection.HttpBase, sessionID: string, hours: number, signal?: AbortSignal) {
  return expectJson(
    await serverFetch(server, `/session/${encodeURIComponent(sessionID)}/snooze`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ hours }),
      signal,
    }),
    decodeSession,
    { error: "Could not update session snooze" },
  )
}

export async function updateSessionNotifyChannels(
  server: ServerConnection.HttpBase,
  sessionID: string,
  notifyChannels: readonly string[],
  options?: { fetch?: typeof globalThis.fetch },
) {
  return expectJson(
    await serverFetch(
      server,
      `/session/${encodeURIComponent(sessionID)}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ notifyChannels }),
      },
      options,
    ),
    decodeSession,
    { error: "Could not update chat notifications" },
  )
}

/** Removes messages from a chat's context in one request: all of them, or none. */
export async function deleteSessionMessages(
  server: ServerConnection.HttpBase,
  sessionID: string,
  messageIDs: readonly string[],
) {
  await expectResponse(
    await serverFetch(server, `/session/${encodeURIComponent(sessionID)}/messages/delete`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ messageIDs }),
    }),
    { error: "Session message deletion failed" },
  )
}
